import { useEffect, useState, type ReactNode } from 'react';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { BarChart3, Eye, LogOut, MessageCircle, RefreshCw } from 'lucide-react';
import { z } from 'zod';

import { TextField } from '@/components/form-field';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldGroup } from '@/components/ui/field';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiDelete, apiGet, apiPost } from '@/lib/api-client';

type SummaryRow = { day: string; pageViews: number; whatsappClicks: number };
type PageRow = { path: string; pageViews: number; whatsappClicks: number };
type AnalyticsSummary = {
  days: number;
  totals: { pageViews: number; whatsappClicks: number };
  daily: SummaryRow[];
  pages: PageRow[];
};

const loginSchema = z.object({ password: z.string().min(1, '請輸入密碼') });

function formatDay(day: string) {
  return new Intl.DateTimeFormat('zh-HK', {
    month: 'short',
    day: 'numeric',
    weekday: 'short',
    timeZone: 'Asia/Hong_Kong',
  }).format(new Date(`${day}T00:00:00+08:00`));
}

function AnalyticsPage() {
  const queryClient = useQueryClient();
  const [days, setDays] = useState(30);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [loginError, setLoginError] = useState('');

  const sessionQuery = useQuery({
    queryKey: ['analytics-session'],
    queryFn: () => apiGet<{ authenticated: boolean }>('/api/analytics/session'),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (sessionQuery.data) setAuthenticated(sessionQuery.data.authenticated);
    if (sessionQuery.isError) setAuthenticated(false);
  }, [sessionQuery.data, sessionQuery.isError]);

  const summaryQuery = useQuery({
    queryKey: ['analytics-summary', days],
    queryFn: () => apiGet<AnalyticsSummary>(`/api/analytics/summary?days=${days}`),
    enabled: authenticated === true,
  });

  const loginMutation = useMutation({
    mutationFn: (password: string) =>
      apiPost<{ authenticated: boolean }>('/api/analytics/session', { password }),
    onSuccess: async () => {
      setAuthenticated(true);
      setLoginError('');
      await queryClient.invalidateQueries({ queryKey: ['analytics-summary'] });
    },
    onError: (error: Error) => setLoginError(error.message),
  });

  const logoutMutation = useMutation({
    mutationFn: () => apiDelete('/api/analytics/session'),
    onSuccess: () => {
      setAuthenticated(false);
      queryClient.removeQueries({ queryKey: ['analytics-summary'] });
    },
  });

  const form = useForm({
    defaultValues: { password: '' },
    validators: { onSubmit: loginSchema },
    onSubmit: ({ value }) => loginMutation.mutateAsync(value.password),
  });

  if (sessionQuery.isPending || authenticated === null) {
    return <div className="grid min-h-svh place-items-center bg-muted text-sm text-muted-foreground">載入中...</div>;
  }

  if (!authenticated) {
    return (
      <main className="grid min-h-svh place-items-center bg-muted p-5">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <BarChart3 className="size-5" />
            </div>
            <CardTitle className="text-xl">網站數據後台</CardTitle>
            <p className="text-sm text-muted-foreground">輸入管理密碼查看瀏覽及 WhatsApp 點擊數據。</p>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                form.handleSubmit();
              }}
            >
              <FieldGroup>
                {loginError ? <p className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{loginError}</p> : null}
                <form.Field name="password">
                  {(field) => (
                    <TextField
                      field={field}
                      label="密碼"
                      type="password"
                      autoComplete="current-password"
                      required
                    />
                  )}
                </form.Field>
                <Field>
                  <form.Subscribe selector={(state) => state.isSubmitting}>
                    {(isSubmitting) => (
                      <Button type="submit" size="lg" disabled={isSubmitting}>
                        {isSubmitting ? '登入中...' : '登入'}
                      </Button>
                    )}
                  </form.Subscribe>
                </Field>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
      </main>
    );
  }

  const summary = summaryQuery.data;
  const today = summary?.daily.find((row) => row.day === new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Hong_Kong', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date())) ?? { pageViews: 0, whatsappClicks: 0 };
  const clickRate = summary?.totals.pageViews
    ? ((summary.totals.whatsappClicks / summary.totals.pageViews) * 100).toFixed(1)
    : '0.0';

  return (
    <main className="min-h-svh bg-muted/40 px-4 py-6 text-foreground sm:px-6 lg:px-10">
      <div className="mx-auto max-w-7xl">
        <header className="flex flex-col gap-5 border-b pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">渡橋創意有限公司</p>
            <h1 className="mt-1 text-2xl font-semibold sm:text-3xl">網站數據</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="analytics-days">查看日數</label>
            <select
              id="analytics-days"
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
              className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
            >
              <option value={7}>最近 7 日</option>
              <option value={30}>最近 30 日</option>
              <option value={90}>最近 90 日</option>
            </select>
            <Button variant="outline" size="icon-lg" onClick={() => summaryQuery.refetch()} aria-label="重新整理">
              <RefreshCw className={summaryQuery.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button variant="outline" size="icon-lg" onClick={() => logoutMutation.mutate()} aria-label="登出">
              <LogOut />
            </Button>
          </div>
        </header>

        <section className="grid gap-4 py-6 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard title="今日瀏覽" value={today.pageViews} icon={Eye} />
          <MetricCard title="今日 WhatsApp 點擊" value={today.whatsappClicks} icon={MessageCircle} />
          <MetricCard title={`${days} 日總瀏覽`} value={summary?.totals.pageViews ?? 0} icon={Eye} />
          <MetricCard title="WhatsApp 點擊率" value={`${clickRate}%`} icon={BarChart3} />
        </section>

        {summaryQuery.isError ? (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {summaryQuery.error.message}
          </p>
        ) : null}

        <section className="grid gap-6 pb-10 xl:grid-cols-2">
          <DataPanel title="每日數據">
            <Table>
              <TableHeader><TableRow><TableHead>日期</TableHead><TableHead className="text-right">頁面瀏覽</TableHead><TableHead className="text-right">WhatsApp 點擊</TableHead></TableRow></TableHeader>
              <TableBody>
                {summary?.daily.map((row) => (
                  <TableRow key={row.day}><TableCell>{formatDay(row.day)}</TableCell><TableCell className="text-right tabular-nums">{row.pageViews}</TableCell><TableCell className="text-right tabular-nums">{row.whatsappClicks}</TableCell></TableRow>
                ))}
                {!summary?.daily.length ? <EmptyRow columns={3} /> : null}
              </TableBody>
            </Table>
          </DataPanel>

          <DataPanel title="各頁面數據">
            <Table>
              <TableHeader><TableRow><TableHead>頁面</TableHead><TableHead className="text-right">瀏覽</TableHead><TableHead className="text-right">WhatsApp 點擊</TableHead></TableRow></TableHeader>
              <TableBody>
                {summary?.pages.map((row) => (
                  <TableRow key={row.path}><TableCell className="max-w-[260px] truncate font-mono text-xs" title={row.path}>{row.path}</TableCell><TableCell className="text-right tabular-nums">{row.pageViews}</TableCell><TableCell className="text-right tabular-nums">{row.whatsappClicks}</TableCell></TableRow>
                ))}
                {!summary?.pages.length ? <EmptyRow columns={3} /> : null}
              </TableBody>
            </Table>
          </DataPanel>
        </section>
      </div>
    </main>
  );
}

function MetricCard({ title, value, icon: Icon }: { title: string; value: string | number; icon: typeof Eye }) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-sm text-muted-foreground">{title}</CardTitle>
        <Icon className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent><p className="text-3xl font-semibold tabular-nums">{value}</p></CardContent>
    </Card>
  );
}

function DataPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border bg-card">
      <h2 className="border-b px-4 py-4 text-base font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function EmptyRow({ columns }: { columns: number }) {
  return <TableRow><TableCell colSpan={columns} className="h-28 text-center text-muted-foreground">暫時未有數據</TableCell></TableRow>;
}

export const Route = createFileRoute('/analytics')({
  head: () => ({
    meta: [
      { title: '網站數據後台 | 渡橋' },
      { name: 'robots', content: 'noindex, nofollow, noarchive' },
    ],
  }),
  component: AnalyticsPage,
});
